import React from 'react';
import { motion } from 'framer-motion';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';

const ArticleCard = ({ article, index, onReadMore }) => {
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date?.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getCategoryLabel = (type) => {
    switch (type) {
      case 'blog': return 'Blog';
      case 'announcement': return 'Annonce';
      default: return 'Article';
    }
  };

  const getCategoryColor = (type) => {
    switch (type) {
      case 'blog': return 'bg-blue-500 text-white';
      case 'announcement': return 'bg-amber-500 text-slate-900';
      default: return 'bg-slate-600 text-white';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
      whileHover={{ y: -8 }}
      onClick={() => onReadMore?.(article)}
      className="bg-white/80 dark:bg-slate-800/50 border border-slate-200 dark:border-white/10 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl hover:shadow-blue-500/10 hover:border-blue-500/30 transition-all duration-300 cursor-pointer group"
    >
      {/* Image Section */}
      <div className="relative h-[180px] overflow-hidden">
        <Image
          src={article?.featuredImage || 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=800'}
          alt={article?.featuredImageAlt || article?.title || 'Article image'}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent" />

        {/* Category Badge - top right */}
        <div className="absolute top-3 right-3">
          <span className={`text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${getCategoryColor(article?.type)}`}>
            {getCategoryLabel(article?.type)}
          </span>
        </div>
      </div>

      {/* Content Area */}
      <div className="p-5">
        {/* Date */}
        <div className="flex items-center gap-1.5 mb-3">
          <Icon name="Calendar" size={13} className="text-blue-400" />
          <span className="text-xs text-slate-500 dark:text-slate-400">
            {formatDate(article?.publishedAt || article?.createdAt)}
          </span>
        </div>

        {/* Title */}
        <h3
          className="text-base font-bold text-slate-900 dark:text-white mb-2 line-clamp-2 group-hover:text-blue-400 transition-colors duration-300"
          style={{ fontFamily: 'var(--font-headline)' }}
        >
          {article?.title}
        </h3>

        {/* Excerpt */}
        <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-2 leading-relaxed mb-4">
          {article?.excerpt || 'Découvrez cet article intéressant...'}
        </p>

        {/* Footer */}
        <div className="flex items-center justify-between pt-3 border-t border-slate-200 dark:border-white/5">
          <div className="flex items-center gap-1.5">
            <Icon name="User" size={13} className="text-slate-500" />
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {article?.authorName || article?.authorRole || 'Admin'}
            </span>
          </div>
          <span className="flex items-center gap-1 text-blue-400 text-xs font-semibold group-hover:gap-2 transition-all duration-300">
            Lire
            <Icon name="ArrowRight" size={14} />
          </span>
        </div>
      </div>
    </motion.div>
  );
};

export default ArticleCard;
